import { APPLY_HREF, PROGRAM_PDF_URL, TICKETS_HREF, slug } from "../lib/site";
import { Body, Em, GOLD, GOLD_DEEP, GhostBtn, GoldBtn, HAIR, Head, Hero, IVORY, Kicker, More, Ornament, Rows, SLATE, SLATE_DIM, Section, Serif, Title } from "../components/ms/kit";
import { PartnerGroups } from "../components/ms/Partners";
import InquiryForm from "../components/ms/InquiryForm";
import PageShell from "../components/ms/PageShell";

const ROLES = [
  ["Angel investors", "Learn the foundations of early-stage investing from active investors, then see how deals are chosen at investor selection meetings and Demo Day."],
  ["Emerging fund managers", "A peer cohort, built with the CVCA and Venture University, for people who want to run a fund. Members work on real companies and hear from institutional investors."],
  ["Founders: startups", "An investor readiness program with in-person bootcamps, office hours with experienced mentors, pitch coaching and a final rehearsal on stage at the Globe Theatre."],
  ["Founders: scaleups", <>MavenCircle, a peer network for founders who are raising larger rounds, growing teams and planning exits, with entrepreneurs who have done it. <More to="/mavencircle">MavenCircle</More></>],
  ["Advisors and speakers", "Mentor founders in office hours, lead a session or join a panel."],
];

const PARTS = [
  ["Investor education", "Live sessions on early-stage investing, financial literacy, term sheets, valuations and SAFEs, syndicates and due diligence, and building a portfolio, taught by active investors.", "I"],
  ["Emerging fund managers", "Built on the Groundwork curriculum, this is where managers draft a fund thesis, screen real founder applications, diligence a company and lead discussion at a selection meeting.", "II"],
  ["Founder readiness", "A structured path from application to bootcamp, mentoring and pitch coaching, with a final rehearsal and the chance to present to investors at Demo Day.", "III"],
  ["Investor selection meetings", "Evenings where angels and the emerging manager cohort hear founders pitch and decide together what deserves a closer look.", "IV"],
  ["Panels and peer sessions", "Conversations on what angel groups look for, going global, and raising, scaling and exiting a company.", "V"],
];

const PARTNER_GROUPS = [
  ["Founder programming partners", "BDC · Neha Bahl, Allium IP · Priya Singh"],
  ["Emerging manager and investor education partners", "Venture University · CVCA · VC Labs"],
  ["Core program partners", "Equivesto · Pulse Law · McKercher LLP · Plug and Play · Whitecap Venture Partners · Canadian Women in Venture Capital"],
  ["Marketing partner", "Prosh Marketing"],
  ["Academic partner", "UTSC Sam Ibrahim Centre (SICIEEIL)"],
  ["Community partner", "Globe Theatre"],
];

const ROUTES = [
  { program: "Investment Readiness", who: "Startup founders", copy: "Bootcamps, office hours with experienced mentors, pitch coaching and a final rehearsal on the Globe Theatre stage.", cta: "Apply below", to: "#founder" },
  { program: "Angel Readiness", who: "Angel investors", copy: "Learn early-stage investing from active investors, then see how deals are chosen at selection meetings and Demo Day.", cta: "Request an invitation", to: "/angels#join" },
  { program: "MavenCircle", who: "Scaleup founders", copy: "Peer sessions with entrepreneurs who have raised, scaled and exited, built around the questions you are facing now.", cta: "Request an invitation", to: "/mavencircle#join" },
  { program: "Groundwork", who: "Emerging fund managers", copy: "This cohort is hand-picked. Stay tuned for the next cohort, and let us know you are interested.", cta: "Express interest", to: "/groundwork#interest" },
];

const RouteCard = ({ program, who, copy, cta, to }) => (
  <div className="flex flex-col p-8 md:p-10" style={{ borderRight: HAIR, borderBottom: HAIR }} data-testid={`program-route-${slug(program)}`}>
    <p className="text-[10px] tracking-[0.32em] uppercase" style={{ color: GOLD_DEEP }}>{who}</p>
    <h3 className="mt-4" style={{ ...Serif, color: IVORY, fontSize: "clamp(1.5rem, 2.4vw, 1.9rem)" }}>{program}</h3>
    <p className="mt-4 flex-1 text-[15px] leading-[1.7]" style={{ color: SLATE }}>{copy}</p>
    <div className="mt-8"><GhostBtn to={to.startsWith("#") ? `/program${to}` : to} testId={`program-route-${slug(program)}-cta`}>{cta}</GhostBtn></div>
  </div>
);

const FOUNDER_FIELDS = [
  { key: "name", label: "Your name and title" },
  { key: "email", label: "Email", type: "email" },
  { key: "company", label: "Company name and website" },
  { key: "city", label: "City and province" },
  { key: "what", label: "What your company does, in a sentence or two", type: "textarea", wide: true },
  { key: "stage", label: "Your stage", options: ["Idea", "Building a product", "Early revenue", "Growing"], wide: true },
  { key: "problem", label: "The problem you solve and who you solve it for", type: "textarea", wide: true },
  { key: "raising", label: "What you are raising, if you are raising", optional: true, wide: true },
  { key: "want", label: "What you most want from the program", options: ["Investor introductions", "Pitch coaching", "Mentoring", "Feedback on your deck"], multi: true, wide: true },
  { key: "heard", label: "How you heard about the program", wide: true },
];

const Apply = () => (
  <Section id="apply" testId="program-apply" width="max-w-4xl">
    <div className="text-center">
      <Kicker>Apply</Kicker>
      <Title size="lg" className="mt-8">Your seat is <Em>waiting.</Em></Title>
      <Body className="mt-8 max-w-2xl mx-auto">Participation is by invitation, and we read every application personally. Choose the route that fits you.</Body>
    </div>
    <div className="mt-16 grid grid-cols-1 md:grid-cols-2" style={{ borderTop: HAIR, borderLeft: HAIR }} data-testid="program-routes">
      {ROUTES.map((r) => <RouteCard key={r.program} {...r} />)}
    </div>
    <div id="founder" className="mt-24 pt-4 text-center scroll-mt-24">
      <Kicker>Investment Readiness · Founder application</Kicker>
      <Body className="mt-6 max-w-2xl mx-auto">Tell us about you and your company. We review every application personally and will be in touch about the next steps.</Body>
    </div>
    <InquiryForm form="founder" fields={FOUNDER_FIELDS} submitLabel="Submit application" testId="founder-form"
      thanks="Your application has reached us. We review every application personally and will be in touch about the next steps." />
    <p className="mt-14 text-center text-[10.5px] tracking-[0.3em] uppercase" style={{ color: SLATE_DIM }}>
      <span style={{ color: GOLD }}>✦</span> Invite-only · Built for the Prairies
    </p>
  </Section>
);

export default function Program() {
  return (
    <PageShell testId="program-page" disclaimer>
      <Hero kicker="Programs" testId="program-hero"
        body="Maven Skies brings investors, fund managers, founders and advisors together across the Prairies to learn, meet and invest. We are the first region of a national initiative to build stronger pathways for founders, angels and emerging fund managers, and these programs are where it begins. Every program is invite-only and built for the Prairies."
        extra={<div className="mt-12 flex flex-wrap justify-center gap-3">
          <GoldBtn to={APPLY_HREF} testId="program-hero-apply">Apply now</GoldBtn>
          {PROGRAM_PDF_URL && <GhostBtn href={PROGRAM_PDF_URL} testId="program-hero-download">Download the program</GhostBtn>}
        </div>}>
        Four programs. <Em>One community.</Em>
      </Hero>
      <Section testId="program-who" width="max-w-5xl"><Head kicker="Who it's for">A place for everyone <Em>who builds.</Em></Head><Rows items={ROLES} testId="program-roles" /></Section>
      <Section testId="program-parts" width="max-w-5xl"><Head kicker="The program">Parts that <Em>feed each other.</Em></Head><Rows items={PARTS} testId="program-parts-list" labelWidth="md:grid-cols-[320px_1fr]" /></Section>
      <Section testId="program-hubs" width="max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-12 md:gap-20 items-start">
          <div><Kicker>Across the Prairies</Kicker><Title className="mt-6">Local front doors, <Em>one community.</Em></Title></div>
          <Body className="md:pt-12">
            Community hubs and roundtables bring the program to founders and investors close to home. Each hub shares the application, nominates companies and welcomes new angels. Iskwewak shares the program through Indigenous networks and leads a session on Indigenous perspectives in entrepreneurship and investment.
          </Body>
        </div>
      </Section>
      <Section testId="program-tech-nights" width="max-w-3xl">
        <div className="text-center" style={{ borderTop: HAIR, borderBottom: HAIR, padding: "4rem 0" }}>
          <Kicker>Tech Nights</Kicker>
          <Body className="mt-8">With the Globe Theatre as our social network partner, Tech Nights are an ongoing series that brings the whole community together around a live production. Tickets are donation-based, so every evening also supports the arts in Regina.</Body>
          <div className="mt-10"><GoldBtn href={TICKETS_HREF} testId="program-reserve-seat">Reserve your seat</GoldBtn></div>
        </div>
      </Section>
      <Section testId="program-partners" width="max-w-5xl">
        <Head kicker="Our partners">With thanks to <Em>our partners.</Em></Head>
        <PartnerGroups groups={PARTNER_GROUPS} />
        <p className="mt-10 max-w-3xl text-[14px] leading-[1.8]" style={{ color: SLATE }}>
          With thanks to the founders, investors and operators who give their time to these programs.
        </p>
        <Ornament />
      </Section>
      <Apply />
    </PageShell>
  );
}
